'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { landingAfterOrgSwitch } from '@mentora/app-shared/organization/org-switch-landing'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@mentora/ui-components'
import { Check, ChevronsUpDown, Layers } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { useTransition } from 'react'

export interface OrgSelectorActions {
  selectOrganization: (input: { organizationId: string | null }) => Promise<ActionResult>
}

export interface OrgSelectorProps {
  /** null with an `allOption` is that "All" selection; null without one is no org at all. */
  activeOrganizationId: string | null
  activeOrganizationName: string | null
  options: Array<{ id: string, name: string }>
  /**
   * The "All" entry: an app admin's "All organizations" (the whole storefront),
   * an owner/admin's "All my orgs" (the orgs they run), or none.
   */
  allOption: 'organizations' | 'mine' | null
  actions: OrgSelectorActions
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0] ?? '')
    .join('')
    .toUpperCase()
}

function Badge({ name }: { name: string | null }) {
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-bold tracking-tight text-primary">
      {name ? initials(name) : <Layers className="size-4" />}
    </div>
  )
}

/**
 * The sidebar header: which organization every org-scoped screen is showing.
 * A plain label when there is nothing to switch to.
 */
export function OrgSelector({ activeOrganizationId, activeOrganizationName, options, allOption, actions }: OrgSelectorProps) {
  const t = useTranslations('orgSelector')
  const { showError } = useActionToast()
  const router = useRouter()
  const pathname = usePathname()
  const [isPending, startTransition] = useTransition()

  const allLabel = allOption === 'mine' ? t('allMyOrganizations') : t('allOrganizations')
  const currentName = activeOrganizationName ?? (allOption ? allLabel : null)
  const switchable = allOption !== null || options.length > 1

  function choose(organizationId: string | null) {
    if (organizationId === activeOrganizationId)
      return
    startTransition(async () => {
      const result = await actions.selectOrganization({ organizationId })
      if (!result.ok) {
        showError(result)
        return
      }
      // The action revalidates every org-scoped path; navigating only moves off
      // a screen that belonged to the previous org.
      router.push(landingAfterOrgSwitch(pathname, organizationId !== null ? 'org' : allOption === 'mine' ? 'mine' : 'all'))
    })
  }

  if (!currentName)
    return null

  const label = (
    <>
      <Badge name={activeOrganizationName} />
      <div className="flex min-w-0 flex-1 flex-col text-left leading-tight">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('label')}</span>
        <span className="truncate text-[13px] font-semibold text-foreground">{currentName}</span>
      </div>
    </>
  )

  if (!switchable) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex items-center gap-2.5 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 [&>div:last-child]:group-data-[collapsible=icon]:hidden">
            {label}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              tooltip={t('switchLabel')}
              aria-label={t('switchLabel')}
              disabled={isPending}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {label}
              <ChevronsUpDown className="ml-auto size-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-96 w-(--radix-dropdown-menu-trigger-width) min-w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t('switchLabel')}</DropdownMenuLabel>
            {allOption && (
              <>
                <DropdownMenuItem onSelect={() => choose(null)}>
                  <Layers className="size-4" />
                  <span className="flex-1 truncate">{allLabel}</span>
                  {activeOrganizationId === null && <Check className="size-4" />}
                </DropdownMenuItem>
                {options.length > 0 && <DropdownMenuSeparator />}
              </>
            )}
            {options.map(org => (
              <DropdownMenuItem key={org.id} onSelect={() => choose(org.id)}>
                <span className="flex-1 truncate">{org.name}</span>
                {org.id === activeOrganizationId && <Check className="size-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
